import { useState } from "react";
import { LogOut } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/Modals";
import { Button } from "@/components/ui/button";
import { dutyOut, getMyAttendanceStatus } from "@/lib/attendanceApi";
import { t } from "@/lib/lang";

/**
 * Sign out, asking about duty (owner's attendance spec: "Explicit logout records
 * duty-out"). A person on duty chooses: end duty and sign out, or only sign out
 * (handing the phone over mid-shift). Someone on a break is told to end it first
 * if they want duty to end. A forced sign-out (expired session, day change) never
 * comes through here, so it never ends anyone's duty.
 */
export function useSignOut(token, logout) {
  const [asking, setAsking] = useState(null);

  const signOut = async () => {
    let status = null;
    try {
      status = await getMyAttendanceStatus(token);
    } catch {
      status = null;   // attendance never stands between a person and signing out
    }
    if (status?.state === "ON_DUTY" || status?.state === "ON_BREAK") setAsking(status);
    else logout();
  };

  const dialog = asking ? (
    <SignOutDialog status={asking} token={token} onCancel={() => setAsking(null)}
      onSignOut={() => { setAsking(null); logout(); }} />
  ) : null;
  return { signOut, dialog };
}

function SignOutDialog({ status, token, onCancel, onSignOut }) {
  const [busy, setBusy] = useState(false);
  const onBreak = status.state === "ON_BREAK";
  const endDuty = async () => {
    setBusy(true);
    try {
      await dutyOut(token);
      toast.success(t("ड्यूटी समाप्त · Duty ended"));
      onSignOut();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Duty did not end. You are still on duty.");
      setBusy(false);
    }
  };
  return (
    <Modal title={t("साइन आउट · Sign out")} onClose={onCancel} testId="sign-out-dialog"
      footer={<Button variant="outline" className="h-11" onClick={onCancel}>{t("रद्द करें · Cancel")}</Button>}>
      <div className="flex flex-col gap-snug">
        {onBreak ? (
          <p className="text-body text-muted-foreground" data-testid="sign-out-on-break">
            {t("आप ब्रेक पर हैं। ड्यूटी खत्म करनी है तो पहले ब्रेक खत्म करें। · You are on a break: end it first to end duty.")}
          </p>
        ) : (
          <Button variant="destructive" className="min-h-14 h-auto py-2 whitespace-normal text-base font-bold" disabled={busy} onClick={endDuty}
            data-testid="sign-out-end-duty">
            <LogOut className="w-5 h-5 mr-snug" />{t("ड्यूटी खत्म करके साइन आउट · End duty & sign out")}
          </Button>
        )}
        <Button variant="outline" className="min-h-14 h-auto py-2 whitespace-normal text-base" disabled={busy} onClick={onSignOut}
          data-testid="sign-out-only">
          {t("सिर्फ साइन आउट (ड्यूटी चालू) · Sign out only, stay on duty")}
        </Button>
      </div>
    </Modal>
  );
}
